import { BookOpenText, GalleryVerticalEnd, LayoutGrid } from 'lucide-react'
import { Link } from 'react-router'
import type { AlbumView } from '@/lib/albumView'
import { cn } from '@/lib/utils'

// A finger-sized target on a phone, with the view's own smaller round inside it.
const item =
  'group grid h-11 min-w-11 place-items-center rounded-full text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:opacity-60 nav:h-9'
const round =
  'flex h-9 min-w-9 items-center justify-center gap-1.5 rounded-full px-2 text-muted-foreground transition-colors group-hover:text-foreground group-aria-pressed:bg-foreground/10 group-aria-pressed:text-foreground nav:px-3'

// Feed, Tiles and Story, as a small quiet group: on a phone it sits in the top bar beside the "+", as icons on a
// frosted round; on a tablet or computer, in the album's own top row with their names. Story opens the album's
// Storybook version, which says what it is on its own page.
export function AlbumViews({
  albumId,
  view,
  onView,
  story,
  glass,
}: {
  albumId: string
  view: AlbumView
  onView: (view: AlbumView) => void
  story: boolean
  glass?: boolean
}) {
  const choice = (value: AlbumView, label: string, Icon: typeof LayoutGrid) => (
    <button type="button" aria-pressed={view === value} title={label} onClick={() => onView(value)} className={item}>
      <span className={round}>
        <Icon className="size-4.5 stroke-[1.75]" aria-hidden />
        <span className="max-nav:sr-only">{label}</span>
      </span>
    </button>
  )
  return (
    <div
      role="group"
      aria-label="Album view"
      className={cn(
        'flex shrink-0 items-center rounded-full nav:gap-0.5 nav:p-1',
        glass
          ? 'bg-background/75 shadow-[0_1px_8px_rgb(0_0_0/0.08)] ring-1 ring-border/70 backdrop-blur-xl backdrop-saturate-150'
          : 'bg-secondary',
      )}
    >
      {choice('feed', 'Feed', GalleryVerticalEnd)}
      {choice('tiles', 'Tiles', LayoutGrid)}
      {story && (
        <Link to={`/album/${albumId}/story`} aria-label="Storybook version" title="Storybook version" className={item}>
          <span className={round}>
            <BookOpenText className="size-4.5 stroke-[1.75]" aria-hidden />
            <span className="max-nav:sr-only">Story</span>
          </span>
        </Link>
      )}
    </div>
  )
}
